import { createBrowserRouter, Navigate, Outlet } from 'react-router'
import { AppErrorPage } from '@/pages/AppError'
import { isLocale, localeDArrivee } from '@/lib/locales'
import { LocaleLayout } from '@/components/LocaleLayout'
import { AuthGuard } from '@/components/auth/AuthGuard'
import { OnboardingGuard } from '@/components/auth/OnboardingGuard'
import { HomePage } from '@/pages/Home'
import { LoginPage } from '@/pages/Login'
import { SignupPage } from '@/pages/Signup'
import { ForgotPasswordPage } from '@/pages/ForgotPassword'
import { ResetPasswordPage } from '@/pages/ResetPassword'
import { VerifyEmailPage } from '@/pages/VerifyEmail'
import { OAuthCallbackPage } from '@/pages/OAuthCallback'
import { OAuthCallbackBridge } from '@/pages/OAuthCallbackBridge'
import { MagicCallbackPage } from '@/pages/MagicCallback'
import { AppliCallbackPage } from '@/pages/AppliCallback'
import { VerificationPage } from '@/pages/Verification'
import { OnboardingMobilePage } from '@/pages/onboarding/Mobile'
import { OnboardingTeamPage } from '@/pages/onboarding/Team'
import { OnboardingStep1Page } from '@/pages/onboarding/Step1'
import { OnboardingStep2Page } from '@/pages/onboarding/Step2'
import { OnboardingWelcomePage } from '@/pages/onboarding/Welcome'
import { OnboardingPhonePage } from '@/pages/onboarding/Phone'
import { OnboardingLanguePage, OnboardingOptionsPage, OnboardingReponsePage, OnboardingUrgencePage } from '@/pages/onboarding/MiseEnService'
import { CheckoutPage } from '@/pages/Checkout'
import { SubscribePage } from '@/pages/Subscribe'
import { ContactPage } from '@/pages/Contact'
import { DashboardPage } from '@/pages/Dashboard'
import { ManagerDashboardPage } from '@/pages/Manager'
import { AnalyticsDrilldownPage } from '@/pages/AnalyticsDrilldown'
import { AgentAnalyticsPage } from '@/pages/AgentAnalytics'
import { AgentSetupPage } from '@/pages/AgentSetup'
import { CallPage } from '@/pages/Call'
import { CallRedirectPage } from '@/pages/CallRedirect'
import { DesktopHandoffPage } from '@/pages/DesktopHandoff'
import { TransferPage } from '@/pages/Transfer'
import { SignByPhonePage } from '@/pages/SignByPhone'
import { NotFoundPage } from '@/pages/NotFound'

function LocaleGate() {
  const segment = window.location.pathname.split('/')[1]
  if (!isLocale(segment)) {
    const rest = window.location.pathname.replace(/^\/+/, '')
    const search = window.location.search || ''
    return <Navigate to={`/${localeDArrivee()}/${rest}${search}`} replace />
  }
  return <Outlet />
}

// Read on each visit, not at module load: the browser language decides.
function RootRedirect() {
  return <Navigate to={`/${localeDArrivee()}`} replace />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
    errorElement: <AppErrorPage />,
  },
  {
    path: '/auth/callback',
    element: <OAuthCallbackBridge />,
    errorElement: <AppErrorPage />,
  },
  {
    // Route universelle click-to-call sans préfixe locale.
    // Ex: https://app.callizy.ca/call?number=+14385551234
    path: '/call',
    element: <CallRedirectPage />,
    errorElement: <AppErrorPage />,
  },
  {
    // Handoff Universal Link OAuth pour Callizy.app (Mac).
    // Ex: https://app.callizy.ca/auth/desktop-handoff?token=...&refresh=...
    path: '/auth/desktop-handoff',
    element: <DesktopHandoffPage />,
    errorElement: <AppErrorPage />,
  },
  {
    path: '/:locale',
    element: (
      <LocaleGate />
    ),
    // Porte toutes les pages de l'app : sans errorElement ici, une erreur de
    // rendu dans le tableau de bord affichait la page de développement de
    // React Router, pile d'appels comprise.
    errorElement: <AppErrorPage />,
    children: [
      {
        element: <LocaleLayout />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'login', element: <LoginPage /> },
          { path: 'signup', element: <SignupPage /> },
          { path: 'forgot-password', element: <ForgotPasswordPage /> },
          { path: 'reset-password', element: <ResetPasswordPage /> },
          { path: 'verify-email', element: <VerifyEmailPage /> },
          { path: 'auth/callback', element: <OAuthCallbackPage /> },
          { path: 'auth/magic-callback', element: <MagicCallbackPage /> },
          // iPhone app, "More settings on the web": one-time code, then the AI agent's page.
          { path: 'auth/appli', element: <AppliCallbackPage /> },
          // Risky sign-in: the code screen, outside AuthGuard (which sends here).
          { path: 'verification', element: <VerificationPage /> },
          { path: 'agent/setup/:token', element: <AgentSetupPage /> },
          // Signature au doigt, ouverte par le code QR de l'ordinateur : sans connexion.
          { path: 'signer/:code', element: <SignByPhonePage /> },
          {
            element: <AuthGuard />,
            children: [
              {
                element: <OnboardingGuard />,
                children: [
                  { path: 'dashboard', element: <DashboardPage /> },
                  { path: 'manager', element: <ManagerDashboardPage /> },
                  { path: 'manager/analytics/drilldown', element: <AnalyticsDrilldownPage /> },
                  { path: 'manager/agents/:userId/analytics', element: <AgentAnalyticsPage /> },
                  { path: 'call', element: <CallPage /> },
                  // Transfert de numéro sur téléphone : une page à part, hors du tableau de bord.
                  { path: 'transfert', element: <TransferPage /> },
                  { path: 'transfert/:transferId', element: <TransferPage /> },
                ],
              },
              // Hors OnboardingGuard : routes accessibles pendant le setup
              // (subscribe/checkout finalisent le paiement, onboarding/* est
              // la cible du redirect — éviter la boucle).
              { path: 'subscribe', element: <SubscribePage /> },
              { path: 'checkout', element: <CheckoutPage /> },
              { path: 'contact', element: <ContactPage /> },
              { path: 'onboarding/step1', element: <OnboardingStep1Page /> },
              { path: 'onboarding/step2', element: <OnboardingStep2Page /> },
              // L'adresse 911 se demande après le paiement (mise en service) : l'ancienne étape 3 renvoie au forfait.
              { path: 'onboarding/step3', element: <Navigate to="../../subscribe" relative="path" replace /> },
              { path: 'onboarding/welcome', element: <OnboardingWelcomePage /> },
              { path: 'onboarding/langue', element: <OnboardingLanguePage /> },
              { path: 'onboarding/reponse', element: <OnboardingReponsePage /> },
              { path: 'onboarding/options', element: <OnboardingOptionsPage /> },
              { path: 'onboarding/urgence', element: <OnboardingUrgencePage /> },
              { path: 'onboarding/phone', element: <OnboardingPhonePage /> },
              { path: 'onboarding/team', element: <OnboardingTeamPage /> },
              { path: 'onboarding/mobile', element: <OnboardingMobilePage /> },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])
